import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { apiRequest } from '../utils/api';
import { useAuthContext } from '../context/AuthContext';
import DailyCashReportPdfDocument from '../components/DailyCashReportPdfDocument';
import SellNotePdfDocument from '../components/SellNotePdfDocument';
import SellNotePdfThreeColumn from '../components/SellNotePdfThreeColumn';
import '../styles/DailyCashReportPage.css';

// 1万円〜1,000円札は100枚/束、500円〜1円硬貨は50枚/棒金。
const DENOMINATIONS = [10000, 5000, 2000, 1000, 500, 100, 50, 10, 5, 1];
const BUNDLE_SIZE = (yen) => (yen >= 1000 ? 100 : 50);
const DISPLAY_CATEGORIES = [
  '1F-PRIZE',
  '1F-TOY',
  '1F-GOODS',
  '2F-PRIZE',
  '2F-TOY',
  '2F-GOODS',
  '免税(現金)',
  'ガチャ',
  'その他(袋)',
  '未分類',
];

// カテゴリー名を比較用に統一
const normalizeCategoryKey = (name) =>
  String(name || '')
    .normalize('NFKC')
    .replace(/[\s\-‐-‒–—―－]/g, '')
    .toUpperCase();

// DBカテゴリー名 → 精算表の表示名
const CATEGORY_NAME_MAP = new Map(
  DISPLAY_CATEGORIES.map(name => [
    normalizeCategoryKey(name),
    name,
  ])
);

// 以前の精算表名で保存した手動修正値も引き継ぐ。
CATEGORY_NAME_MAP.set('1Fプライズ', '1F-PRIZE');
CATEGORY_NAME_MAP.set('2Fプライズ', '2F-PRIZE');
CATEGORY_NAME_MAP.set('GACHA', 'ガチャ');
CATEGORY_NAME_MAP.set('ETC', 'その他(袋)');

// 一致しないカテゴリーは未分類
const getDisplayCategoryName = (name) =>
  CATEGORY_NAME_MAP.get(normalizeCategoryKey(name)) || '未分類';
const formatYen = (value) => `¥${Math.round(Number(value) || 0).toLocaleString('ja-JP')}`;
const money = (value) => Number(value) || 0;
const localDateString = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const previousDateString = (date) => {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return localDateString(d);
};
const storageKey = (registerId, date) => `pasomaru:daily-cash-report:v1:${registerId}:${date}`;
const emptyLine = (label = '') => ({ id: `${Date.now()}-${Math.random()}`, label, amount: '' });
const emptyMovement = () => ({ ...emptyLine(), direction: 'out' });

function getSavedReport(registerId, date) {
  if (!registerId || !date) return null;
  try {
    const raw = localStorage.getItem(storageKey(registerId, date));
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('精算表の保存データが読めません', error);
    return null;
  }
}

function initialDraft(registerId, date) {
  const saved = getSavedReport(registerId, date);
  return {
    counts: Object.fromEntries(DENOMINATIONS.map(denom => [denom, { bundles: '', pieces: '' }])),
    todayOverride: null,
    salesOverrides: {},
    appTaxExemptAmount: '',
    purchases: [emptyLine('ポート商会')],
    buybacks: [emptyLine('プライズ'), emptyLine('TOY'), emptyLine('その他')],
    expenses: [emptyLine('')],
    movements: [emptyMovement()],
    normalTaxOverride: null,
    exemptProvisionalTaxOverride: null,
    ...saved,
  };
}

const sumLines = (lines) => (lines || []).reduce((sum, line) => sum + money(line.amount), 0);
const sumMovements = (lines, direction) =>
  (lines || []).filter(x => x.direction === direction).reduce((sum, x) => sum + money(x.amount), 0);

// 入力欄でEnterを押したら、次の入力欄へ（ボタンは飛ばす）。
// STEP1の金種入力だけは下の専用順序を優先する。
function focusNextEditableField(currentInput) {
  const page = currentInput.closest('.dailyCash-page');
  if (!page) return;

  const fields = Array.from(
    page.querySelectorAll('input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled)')
  ).filter(element => element.getClientRects().length > 0);
  const position = fields.indexOf(currentInput);
  const next = position >= 0 ? fields[position + 1] : null;
  if (next) {
    next.focus();
    if (next.tagName === 'INPUT') next.select();
  }
}

function MoneyInput({ value, onChange, className = '', disabled = false, inputRef, onEnter }) {
  return (
    <input
      ref={inputRef}
      className={`dailyCash-money ${className}`}
      // number型はフォーカス中のマウスホイールで値が増減するため、整数専用のtext型を使用。
      // 数値キーボードはinputModeで維持する。
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={value ?? ''}
      onChange={event => {
        const nextValue = event.target.value;
        if (/^-?\d*$/.test(nextValue)) onChange(nextValue);
      }}
      onKeyDown={event => {
        if (event.key !== 'Enter' || event.nativeEvent?.isComposing) return;
        event.preventDefault();
        if (onEnter) onEnter(event);
        else focusNextEditableField(event.currentTarget);
      }}
      disabled={disabled}
    />
  );
}

function LineSection({ title, lines, updateLines, locations, movement = false }) {
  const change = (id, key, value) => updateLines(lines.map(x => x.id === id ? { ...x, [key]: value } : x));
  return (
    <section className="dailyCash-section">
      <div className="dailyCash-sectionTitle"><h3>{title}</h3><strong>{formatYen(movement ? sumMovements(lines, 'in') - sumMovements(lines, 'out') : sumLines(lines))}</strong></div>
      {(lines || []).map(line => <div className="dailyCash-line" key={line.id}>
        {movement && <select aria-label="移動区分" value={line.direction} onChange={e => change(line.id, 'direction', e.target.value)}>
          <option value="in">移動入金</option><option value="out">移動出金</option>
        </select>}
        {movement ? <select aria-label="移動元・移動先" value={line.label} onChange={e => change(line.id, 'label', e.target.value)}>
          <option value="">場所を選択</option>
          {[...new Set([...(locations || []), line.label].filter(Boolean))].map(x => <option value={x} key={x}>{x}</option>)}
        </select> : <input type="text" aria-label={`${title}の内容`} placeholder="内容を入力" value={line.label} onChange={e => change(line.id, 'label', e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent?.isComposing) { e.preventDefault(); focusNextEditableField(e.currentTarget); } }} />}
        <MoneyInput value={line.amount} onChange={val => change(line.id, 'amount', val)} />
        <button type="button" className="dailyCash-remove" title="行を削除" onClick={() => updateLines(lines.filter(x => x.id !== line.id))}>×</button>
      </div>)}
      <button type="button" className="dailyCash-smallButton" onClick={() => updateLines([...(lines || []), movement ? emptyMovement() : emptyLine()])}>＋ 追加</button>
    </section>
  );
}

// SalesSummaryModal の正式清算APIへ送る値と同じ項目。
// 金額は「¥ 1,234」の文字列で返るので数値へ変換。
const closingMoney = (value) => {
  const n = Number(String(value ?? '0').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

function buildClosingPayload(salesData, registerId, lastClosingDate) {
  if (!Array.isArray(salesData?.normal_sales_by_category) ||
      !Array.isArray(salesData?.tax_exempt_sales_by_category)) {
    throw new Error('正式清算用の売上データを取得できませんでした。');
  }

  const makeCategories = (items) => items.map(cat => ({
    category_id: cat.category_id,
    sales: closingMoney(cat.total_sales),
    transactions: Number(cat.total_transactions) || 0,
    cash_sales: closingMoney(cat.cash),
    cashless_sales: closingMoney(cat.cashless),
  }));

  return {
    register_id: registerId,
    latest_closing_date: lastClosingDate || null,
    total_sales: closingMoney(salesData.total_sales_all),
    total_tax: closingMoney(salesData.tax_amount),
    total_discount: closingMoney(salesData.discount_total),
    total_transactions: Number(salesData.total_receipts) || 0,
    total_items: Number(salesData.total_sales_count) || 0,
    normal_sales_by_category: makeCategories(salesData.normal_sales_by_category),
    tax_exempt_sales_by_category: makeCategories(salesData.tax_exempt_sales_by_category),
  };
}

function DailyCashEditor({ date, registerId }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState(() => initialDraft(registerId, date));
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [printing, setPrinting] = useState(false);
  const [printingReceipt, setPrintingReceipt] = useState(false);
  const [batchStatus, setBatchStatus] = useState('');

  // 金種入力の移動順：本数10,000円→…→1円、次に枚数10,000円→…→1円。
  const cashCountInputRefs = useRef({ bundles: {}, pieces: {} });
  const handleCashCountEnter = (denom, field, event) => {
    const index = DENOMINATIONS.indexOf(denom);
    const nextDenom = DENOMINATIONS[index + 1];
    const nextInput = nextDenom !== undefined
      ? cashCountInputRefs.current[field][nextDenom]
      : field === 'bundles'
        ? cashCountInputRefs.current.pieces[DENOMINATIONS[0]]
        : null;

    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    } else {
      // 枚数の最終欄が終わったら「本日」の金額欄へ。
      focusNextEditableField(event.currentTarget);
    }
  };

  useEffect(() => {
    let disposed = false;
    setLoading(true);
    setError('');
    apiRequest(`inventory/daily_cash_report/source/?date=${encodeURIComponent(date)}&register_id=${encodeURIComponent(registerId)}`)
      .then(data => {
        if (disposed) return;
        if (!data || !Object.prototype.hasOwnProperty.call(data, 'normal_cash_tax')
            || !Object.prototype.hasOwnProperty.call(data, 'tax_exempt_provisional_cash_tax')
            || !Object.prototype.hasOwnProperty.call(data, 'tax_exempt_sales_total')) {
          setError('当日の免税額または税区分の取得に失敗しました。Djangoの daily_cash_report.py を当日免税額対応版にしてください。');
          return;
        }
        setSource(data);
      })
      .catch(err => { if (!disposed) setError(err?.details?.error || '売上の自動取得に失敗しました。APIの追加を確認してください。'); })
      .finally(() => { if (!disposed) setLoading(false); });
    return () => { disposed = true; };
  }, [date, registerId]);

  // 開始現金は「前回の正式清算日の翌日繰越」を優先する。
  // 清算が抜けた日の入力データを誤って繰越に使わない。
  const closingDay = source?.last_closing_at?.slice(0, 10);
  const previous = closingDay && closingDay < date
    ? getSavedReport(registerId, closingDay)
    : getSavedReport(registerId, previousDateString(date));
  const carriedToday = money(previous?.nextAmount);
  const today = draft.todayOverride === null ? carriedToday : money(draft.todayOverride);

  const countedTotal = useMemo(() => DENOMINATIONS.reduce((sum, denom) => {
    const count = draft.counts?.[denom] || {};
    return sum + denom * (money(count.bundles) * BUNDLE_SIZE(denom) + money(count.pieces));
  }, 0), [draft.counts]);
  const nextAmount = Math.floor(countedTotal / 1000) * 1000;
  const remainder = countedTotal - nextAmount;

  const categories = useMemo(() => {

  // 表示する10カテゴリーを0円で初期化
  const amounts = Object.fromEntries(
    DISPLAY_CATEGORIES.map(name => [name, 0])
  );

  // POS売上を正式な表示カテゴリーに振り分け
  for (const cat of source?.categories || []) {

    const name = getDisplayCategoryName(cat.name);

    amounts[name] += money(cat.amount);
  }

  // 保存済みの手動修正値
  const overrides = {};

  for (const [savedName, value] of Object.entries(
    draft.salesOverrides || {}
  )) {
    const name = getDisplayCategoryName(savedName);

    // 旧データの未知のカテゴリー別修正値は
    // 未分類の合計へ勝手に適用しない
    if (
      name === '未分類' &&
      normalizeCategoryKey(savedName) !== '未分類'
    ) {
      continue;
    }

    overrides[name] = value;
  }

  return DISPLAY_CATEGORIES.map(name => {

    const automatic = amounts[name];

    const inputValue =
      overrides[name] !== undefined
        ? overrides[name]
        : automatic;

    return {
      name,
      automatic,
      amount: money(inputValue),
      inputValue,
    };
  });

}, [source, draft.salesOverrides]);
  const salesTotal = categories.reduce((sum, c) => sum + c.amount, 0);
  const buybackTotal = sumLines(draft.buybacks);
  const purchaseTotal = sumLines(draft.purchases);
  const expensesTotal = sumLines(draft.expenses);
  const movementIn = sumMovements(draft.movements, 'in');
  const movementOut = sumMovements(draft.movements, 'out');
  // 返品の現金返金はレジに登録済みの入出金から自動反映（経費とは別扱い）。
  const cashRefund = money(source?.cash_transactions?.refund);
  // 「利益」は手元現金の増減額を表す（移動入出金を含む）。
  const profit = salesTotal - buybackTotal - purchaseTotal - expensesTotal + movementIn - movementOut - cashRefund;
  // 現金で受け取った税額のみを加算。税抜の販売額との二重計上を避ける。
  const normalCashTaxAutomatic = money(source?.normal_cash_tax);
  const exemptProvisionalTaxAutomatic = money(source?.tax_exempt_provisional_cash_tax);
  const normalCashTax = draft.normalTaxOverride === null
    ? normalCashTaxAutomatic : money(draft.normalTaxOverride);
  const exemptProvisionalCashTax = draft.exemptProvisionalTaxOverride === null
    ? exemptProvisionalTaxAutomatic : money(draft.exemptProvisionalTaxOverride);
  const tax = normalCashTax + exemptProvisionalCashTax;
  const expected = today + profit + tax;
  const overShort = countedTotal - expected;

  // STEP1ではPOSの入出金を使った暫定見込を表示（STEP2の手入力で確定）。
  const provisional = today + money(source?.cash_sales_gross)
    + money(source?.cash_transactions?.deposit)
    - money(source?.cash_transactions?.withdrawal)
    - money(source?.cash_transactions?.expenses)
    - money(source?.cash_transactions?.purchase_payment)
    - money(source?.cash_transactions?.purchase)
    - money(source?.cash_transactions?.refund);

  const saveDraft = () => {
    const data = { ...draft, nextAmount, countedTotal, savedAt: new Date().toISOString() };
    try {
      localStorage.setItem(storageKey(registerId, date), JSON.stringify(data));
      return true;
    } catch (e) {
      setError('ローカルストレージへ保存できませんでした。空き容量やブラウザの設定を確認してください。');
      return false;
    }
  };

  const setField = (field, value) => setDraft(old => ({ ...old, [field]: value }));
  const setCounts = (denom, field, value) => setDraft(old => ({
    ...old,
    counts: { ...old.counts, [denom]: { ...old.counts?.[denom], [field]: value } },
  }));
  const setSalesOverride = (name, value) => {
    setDraft(old => ({
      ...old,
      salesOverrides: {
        ...Object.fromEntries(
          Object.entries(old.salesOverrides || {}).filter(
            ([key]) => getDisplayCategoryName(key) !== name
          )
        ),
        [name]: value,
      },
    }));
  };

  const moveStep = nextStep => {
    if (!saveDraft()) return;
    setStep(nextStep);
    document.querySelector('.dailyCash-page')?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // STEP1から清算記録を作らずに清算レシートだけ印刷する。
  // 印刷する内容は指定日の指定レジの確定伝票を再集計したもの。
  const printSettlementPreview = async (goToStep2 = false) => {
    if (printingReceipt || printing) return;
    if (!saveDraft()) return;

    setPrintingReceipt(true);
    setError('');
    try {
      if (!window.electronAPI?.settlementPrint) {
        throw new Error('清算レシート印刷はElectronから実行してください。');
      }

      // 同一日のデータを直前に取り直す。正式な清算POSTは呼ばない。
      const [receiptData, latestSource] = await Promise.all([
        apiRequest(
          `inventory/register_closing/preview/?date=${encodeURIComponent(date)}&register_id=${encodeURIComponent(registerId)}`,
          'GET'
        ),
        apiRequest(
          `inventory/daily_cash_report/source/?date=${encodeURIComponent(date)}&register_id=${encodeURIComponent(registerId)}`,
          'GET'
        ),
      ]);
      if (!receiptData?.is_preview) {
        throw new Error('仮印刷用のデータを取得できませんでした。');
      }
      if (!latestSource || !Object.prototype.hasOwnProperty.call(latestSource, 'normal_cash_tax')
          || !Object.prototype.hasOwnProperty.call(latestSource, 'tax_exempt_provisional_cash_tax')
          || !Object.prototype.hasOwnProperty.call(latestSource, 'tax_exempt_sales_total')) {
        throw new Error('日次精算表の最新売上を取得できませんでした。');
      }

      const result = await window.electronAPI.settlementPrint(receiptData);
      if (result?.success !== true) {
        throw new Error(result?.error || result?.reason || 'プリンターへの印刷処理に失敗しました。');
      }
      setSource(latestSource);
      if (goToStep2) {
        // 入力値・自動販売額は維持し、STEP2へ。
        setStep(2);
        document.querySelector('.dailyCash-page')?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      console.error('清算レシート仮印刷:', e);
      setError(`清算レシートの仮印刷に失敗しました：${e.message}`);
    } finally {
      setPrintingReceipt(false);
    }
  };

  // いずれのボタンもこのレポートを使用（PDF・Excelは同じ集計値）。
  const buildDailyCashReport = () => ({
    date, registerId, registerName: source?.register_name || `レジ${registerId}`,
    periodStart: source?.period_start || null,
    periodEnd: source?.period_end || null,
    denominations: DENOMINATIONS.map(denom => ({
      denom,
      bundles: money(draft.counts?.[denom]?.bundles),
      pieces: money(draft.counts?.[denom]?.pieces),
      amount: denom * (money(draft.counts?.[denom]?.bundles) * BUNDLE_SIZE(denom)
        + money(draft.counts?.[denom]?.pieces)),
    })),
    countedTotal, today, categories, salesTotal, buybacks: draft.buybacks,
    purchases: draft.purchases, expenses: draft.expenses, movements: draft.movements,
    buybackTotal, purchaseTotal, expensesTotal, movementIn, movementOut,
    profit, normalCashTax, exemptProvisionalCashTax, tax, cashRefund,
    remainder, overShort, nextAmount,
  });

  // Excel保存とB5直接印刷（プレビューなし）。失敗は呼び出し元へ返す。
  const saveAndPrintB5 = async () => {
    if (!saveDraft()) throw new Error('日次精算表の保存に失敗しました。');
    if (!window.electronAPI?.printDailyCashB5 ||
        !window.electronAPI?.exportDailyCashExcel ||
        !window.electronAPI?.getPrintConfig) {
      throw new Error('Electronの直接印刷・Excel保存APIが未登録です。Electronを更新してください。');
    }

    const report = buildDailyCashReport();
    const settings = await window.electronAPI.getPrintConfig();
    if (!settings?.dailyCashExcelFolder) {
      throw new Error('各種設定で「精算表Excel保存先」を指定してください。');
    }

    // 先にB5 PDFを作成しておく。Excelの保存に失敗した場合は印刷しない。
    const blob = await pdf(<DailyCashReportPdfDocument report={report} />).toBlob();
    const excelResult = await window.electronAPI.exportDailyCashExcel(
      report, settings.dailyCashExcelFolder
    );
    if (excelResult?.success !== true) {
      throw new Error('Excelの保存に失敗しました。');
    }

    const printResult = await window.electronAPI.printDailyCashB5(await blob.arrayBuffer());
    if (printResult?.success !== true) {
      throw new Error(printResult?.error || 'B5精算表を印刷できませんでした。');
    }
    return excelResult;
  };

  const onPrint = async () => {
    if (printing || printingReceipt) return;
    setPrinting(true);
    setError('');
    try {
      await saveAndPrintB5();
    } catch (e) {
      setError(`精算表の出力に失敗しました：${e.message}`);
    } finally {
      setPrinting(false);
    }
  };

  // 一括処理は正式清算を含む。途中失敗時にPOSTを重ねないよう、状態を日付・レジ別に記録。
  const batchKey = `pasomaru:daily-cash-batch:v1:${registerId}:${date}`;

  const onPrintAndCloseAll = async () => {
    if (printing || printingReceipt) return;
    if (!window.electronAPI?.printDailyCashSellNote ||
        !window.electronAPI?.journalPrint ||
        !window.electronAPI?.settlementPrint) {
      setError('一括処理に必要なElectron印刷APIが見つかりません。');
      return;
    }
    if (date !== localDateString()) {
      setError('正式清算は本日の日次精算表から実行してください。過去日付の清算は行いません。');
      return;
    }

    let progress = {};
    try {
      progress = JSON.parse(localStorage.getItem(batchKey) || '{}');
      if (progress.closingStatus === 'attempted') {
        throw new Error('正式清算を送信済みですが、結果が未確認です。清算履歴で完了を確認するまで再実行できません。二重清算を防いでいます。');
      }
    } catch (err) {
      setError(err?.message || '一括処理の状態を確認できません。');
      return;
    }

    const continuing = progress.closingStatus === 'done';
    const confirmed = window.confirm(
      continuing
        ? '前回の清算は実行済みです。未印刷の書類だけ再開しますか？'
        : 'B5精算表・Excel保存後、正式清算を実行して清算レシート、ジャーナル、販売ノートを印刷します。\n正式清算は取り消せません。実行しますか？'
    );
    if (!confirmed) return;

    const persist = (updates) => {
      progress = { ...progress, ...updates, updatedAt: new Date().toISOString() };
      localStorage.setItem(batchKey, JSON.stringify(progress));
    };

    setPrinting(true);
    setError('');
    setBatchStatus('データを確認しています…');
    try {
      if (!saveDraft()) throw new Error('入力内容を保存できませんでした。');

      // 再開時は確定済み清算IDを使って「同じ清算期間」を再取得する。
      // 前回清算の更新後に通常の未清算期間でノートを集計すると0件になるため。
      if (continuing && !progress.closingId) {
        const res = await apiRequest(
          `inventory/register_closing_list_for_print/?date=${encodeURIComponent(date)}&register_id=${encodeURIComponent(registerId)}`, 'GET'
        );
        const closings = Array.isArray(res) ? res : (res?.results ?? []);
        const latest = [...closings].sort((a, b) => Number(b.id) - Number(a.id))[0];
        if (!latest?.id) throw new Error('清算済みですが、再開する清算IDを確認できません。');
        persist({ closingId: latest.id });
      }
      // ノート用データを正式清算前に取得（清算後の再開時は清算IDで期間確定）。
      const noteQuery = `date=${encodeURIComponent(date)}&register_id=${encodeURIComponent(registerId)}`
        + (continuing ? `&closing_id=${encodeURIComponent(progress.closingId)}` : '');
      const noteData = await apiRequest(
        `inventory/note_sales_details_by_date/?${noteQuery}`, 'GET'
      );
      if (!noteData || typeof noteData !== 'object') {
        throw new Error('販売ノートの明細を取得できませんでした。');
      }
      // 取引履歴・清算管理の「ノート」ボタンと同じPDFコンポーネント選択。
      // 両画面で読み込むモジュールを揃え、フォント登録条件の違いを減らす。
      const noteComponent = Number(registerId) === 2
        ? <SellNotePdfDocument date={date} registerId={registerId} summaryData={noteData} />
        : <SellNotePdfThreeColumn date={date} registerId={registerId} summaryData={noteData} />;
      const noteBlob = await pdf(noteComponent).toBlob();
      const notePdfBytes = await noteBlob.arrayBuffer();

      // 正式清算に使う集計は、清算前に取得する（清算後は集計期間が変わるため）。
      let closingPayload = null;
      if (!continuing) {
        const [currentSales, latestClosing] = await Promise.all([
          apiRequest(`inventory/sales_summary/?register_id=${encodeURIComponent(registerId)}`, 'GET'),
          apiRequest(`inventory/register_closings/latest_closing_date_by_register/?register_id=${encodeURIComponent(registerId)}`, 'GET'),
        ]);
        closingPayload = buildClosingPayload(
          currentSales, registerId, latestClosing?.latest_closing_date || new Date('2025-01-01T00:00:00+09:00').toISOString()
        );
      }

      if (!progress.b5Printed) {
        setBatchStatus('Excel保存・B5精算表を直接印刷しています…');
        await saveAndPrintB5();
        persist({ b5Printed: true });
      }

      if (!continuing) {
        // 正式清算POST直前に「送信中」を永続化。通信失敗時は安全側で停止する。
        persist({ closingStatus: 'attempted' });
        setBatchStatus('正式清算を実行しています…');
        const result = await apiRequest('inventory/register/closing/', 'POST', closingPayload);
        const id = result?.id ?? result?.closing_id ?? result?.data?.id ?? result?.closing?.id;
        persist({ closingStatus: 'done', closingId: id || null });
      }

      // 閉店したという事実は既に記録済み。以下の失敗では清算をやり直さない。
      if (!progress.closingId) {
        const res = await apiRequest(
          `inventory/register_closing_list_for_print/?date=${encodeURIComponent(date)}&register_id=${encodeURIComponent(registerId)}`,
          'GET'
        );
        const closings = Array.isArray(res) ? res : (res?.results ?? []);
        const latest = [...closings].sort((a, b) => Number(b.id) - Number(a.id))[0];
        if (!latest?.id) throw new Error('正式清算は完了しましたが、清算IDを取得できません。清算履歴を確認してください。');
        persist({ closingId: latest.id });
      }

      if (!progress.settlementPrinted) {
        setBatchStatus('正式清算レシートを印刷しています…');
        const receipt = await apiRequest(
          `inventory/register_closing/print/?date=${encodeURIComponent(date)}&register_id=${encodeURIComponent(registerId)}`,
          'GET'
        );
        const result = await window.electronAPI.settlementPrint(receipt);
        if (result?.success !== true) throw new Error('清算レシート印刷に失敗しました。');
        persist({ settlementPrinted: true });
      }

      if (!progress.journalPrinted) {
        setBatchStatus('ジャーナルを印刷しています…');
        const journal = await apiRequest(
          `inventory/journal/print/?closing_id=${encodeURIComponent(progress.closingId)}`, 'GET'
        );
        if (!Array.isArray(journal)) throw new Error('ジャーナルデータが配列ではありません。');
        const result = await window.electronAPI.journalPrint(journal);
        if (result?.success !== true) throw new Error('ジャーナル印刷に失敗しました。');
        persist({ journalPrinted: true });
      }

      if (!progress.notePrinted) {
        setBatchStatus('販売ノートを印刷しています…');
        const result = await window.electronAPI.printDailyCashSellNote(notePdfBytes);
        if (result?.success !== true) throw new Error('販売ノート印刷に失敗しました。');
        persist({ notePrinted: true });
      }

      setBatchStatus('Excel保存・B5印刷・正式清算・清算レシート・ジャーナル・販売ノートの処理を完了しました。');
      // すべて正常終了したときだけ、OKで日次精算表を閉じて取引履歴へ戻す。
      // window.close() はElectronのメイン画面まで閉じてしまうため使用しない。
      window.alert('終了しました。');
      navigate('/transaction_history', { replace: true });
    } catch (e) {
      const alreadyClosed = progress.closingStatus === 'done';
      setError(`${alreadyClosed ? '正式清算は完了しています。再清算しないでください。' : progress.closingStatus === 'attempted' ? '正式清算の送信結果が未確認です。履歴で確認してください。' : '正式清算前に停止しました。'}\n処理エラー：${e.message}`);
      setBatchStatus('途中で停止しました。印刷状況を確認してください。');
      console.error('日次精算表一括処理:', e);
    } finally {
      setPrinting(false);
    }
  };

  // ==========================================
  // 免税送信アプリとの金額照合
  // ==========================================

  // POSの当日免税売上合計
  const posTaxExemptAmount = money(
      source?.tax_exempt_sales_total
  );

  // アプリ免税額が入力されているか
  const hasAppTaxExemptAmount =
      draft.appTaxExemptAmount !== '' &&
      draft.appTaxExemptAmount !== null &&
      draft.appTaxExemptAmount !== undefined;

  // アプリ免税額
  const appTaxExemptAmount = money(
      draft.appTaxExemptAmount
  );

  // 差額（アプリ - POS）
  const taxExemptDifference = hasAppTaxExemptAmount
      ? appTaxExemptAmount - posTaxExemptAmount
      : null;

  if (loading) return <div className="dailyCash-page"><h2>日次精算表</h2><p>売上データを取得中...</p></div>;
  if (error && !source) return <div className="dailyCash-page"><h2>日次精算表</h2><p className="dailyCash-error">{error}</p><button onClick={() => navigate(-1)}>戻る</button></div>;

  return <div className="dailyCash-page">
    <header className="dailyCash-header">
      <div><h2>日次精算表（B5）</h2><p>{date}　{source?.register_name || `レジ${registerId}`}</p>
        <p style={{fontSize: '0.85em'}}>
          集計期間：{source?.period_start ? source.period_start.replace('T', ' ').slice(0, 16) : '初回（全履歴）'}
          {' ～ '}{source?.period_end?.replace('T', ' ').slice(0, 16) || '現在'}
        </p>
      </div>
      <button type="button" onClick={() => navigate(-1)}>閉じる</button>
    </header>
    {error && <p className="dailyCash-error">{error}</p>}
    <div className="dailyCash-steps"><span className={step === 1 ? 'active' : ''}>① 現金確認</span><span className={step === 2 ? 'active' : ''}>② 内訳入力</span><span className={step === 3 ? 'active' : ''}>③ 印刷</span></div>

    {step === 1 && <>

      {/* =====================================
          免税送信アプリとの照合
      ===================================== */}
      <section className="dailyCash-section">

          <h3>① 免税確認</h3>

          <div className="dailyCash-totals">

              {/* POS免税売上 */}
              <div>
                  <span>合計免税額（POS自動取得・当日分）</span>
                  <strong>
                      {formatYen(posTaxExemptAmount)}
                  </strong>
              </div>

              {/* アプリ免税額 */}
              <div>
                  <span>アプリ免税額</span>

                  <MoneyInput
                    value={draft.appTaxExemptAmount}
                    onChange={v => setField('appTaxExemptAmount', v)}
                  />
              </div>

              {/* 差額表示 */}
              {taxExemptDifference !== null && (

                  <div>
                      <span>差額（アプリ − POS）</span>

                      {taxExemptDifference === 0 ? (

                          <strong style={{ color: '#66dd88' }}>
                              ✅ 免税金額一致
                          </strong>

                      ) : (

                          <strong className="dailyCash-variance">
                              {taxExemptDifference > 0 ? '+' : '-'}
                              {formatYen(Math.abs(taxExemptDifference))}
                          </strong>

                      )}
                  </div>

              )}

          </div>

          <p className="dailyCash-help">
              ※この免税確認だけは表示日当日分（現金・キャッシュレス合計）を比較します。
              ほかの清算集計は前回の正式清算以降です。
          </p>

      </section>

      {/* =====================================
          現金確認
      ===================================== */}

      <h3>② 現金の本数・枚数を入力</h3>
      <table className="dailyCash-countTable"><thead><tr><th>金種</th><th>本数（100枚/束・50枚/棒金）</th><th>枚数</th><th>金額</th></tr></thead><tbody>
        {DENOMINATIONS.map(denom => <tr key={denom}>
          <th>{denom.toLocaleString('ja-JP')}円</th>
          <td>
            <MoneyInput
              value={draft.counts?.[denom]?.bundles}
              onChange={val => setCounts(denom, 'bundles', val)}
              inputRef={el => { cashCountInputRefs.current.bundles[denom] = el; }}
              onEnter={event => handleCashCountEnter(denom, 'bundles', event)}
            />
          </td>
          <td>
            <MoneyInput
              value={draft.counts?.[denom]?.pieces}
              onChange={val => setCounts(denom, 'pieces', val)}
              inputRef={el => { cashCountInputRefs.current.pieces[denom] = el; }}
              onEnter={event => handleCashCountEnter(denom, 'pieces', event)}
            />
          </td>
          <td>{formatYen(denom * (money(draft.counts?.[denom]?.bundles) * BUNDLE_SIZE(denom) + money(draft.counts?.[denom]?.pieces)))}</td>
        </tr>)}
      </tbody><tfoot><tr><th colSpan="3">実際の現金合計</th><th>{formatYen(countedTotal)}</th></tr></tfoot></table>
      <section className="dailyCash-section">
        <h3>本日（前回清算日の「翌日」を自動引継ぎ）</h3>
        <div className="dailyCash-line">
          <MoneyInput value={draft.todayOverride === null ? carriedToday : draft.todayOverride} onChange={v => setField('todayOverride', v)} />
          <button className="dailyCash-smallButton" onClick={() => setField('todayOverride', null)}>前日から自動取得</button>
        </div>
        {!previous && <p className="dailyCash-help">前日のデータがないため、初日のみ「本日」の金額を入力してください。</p>}
      </section>
      <div className="dailyCash-totals"><div>POS入出金からの暫定残高 <strong>{formatYen(provisional)}</strong></div><div>実際の現金との差（暫定） <strong className={countedTotal - provisional !== 0 ? 'dailyCash-variance' : ''}>{formatYen(countedTotal - provisional)}</strong></div></div>
      <p className="dailyCash-help">※買取・仕入れ・経費・移動は次画面で手入力します。正式な過不足は内訳入力後に確定します。</p>
      <p className="dailyCash-help">※清算レシートは仮印刷です。清算記録は保存されず、正式な清算にはなりません。印刷後にSTEP2で内容を確認できます。</p>
      <div className="dailyCash-actions" style={{ flexWrap: 'wrap' }}>
        <button type="button" className="secondary" disabled={printingReceipt} onClick={() => moveStep(2)}>
          印刷せず内訳入力へ →
        </button>
        <button type="button" disabled={printingReceipt} onClick={() => printSettlementPreview(true)}>
          {printingReceipt ? '清算レシート印刷中...' : '清算レシートを仮印刷して内訳入力へ →'}
        </button>
      </div>
    </>}

    {step === 2 && <>
      <section className="dailyCash-section">
        <div className="dailyCash-sectionTitle"><h3>販売（現金分・自動入力／修正可）</h3><strong>{formatYen(salesTotal)}</strong></div>
        {categories.map(cat => <div className="dailyCash-line" key={cat.name}>
          <span className="dailyCash-label">{cat.name}</span>
          <MoneyInput value={cat.inputValue} onChange={v => setSalesOverride(cat.name, v)} />
          <button
            title="自動入力に戻す"
            className="dailyCash-smallButton"
            onClick={() => setDraft(old => ({
              ...old,
              salesOverrides: Object.fromEntries(
                Object.entries(old.salesOverrides || {}).filter(
                  ([key]) => getDisplayCategoryName(key) !== cat.name
                )
              ),
            }))}
          >
            ↺
          </button>
        </div>)}
        <p className="dailyCash-help">※キャッシュレス売上は現金残高の計算に含みません。修正しても販売履歴は変わりません。</p>
      </section>
      <LineSection title="買取" lines={draft.buybacks} updateLines={x => setField('buybacks', x)} />
      <LineSection title="仕入れ" lines={draft.purchases} updateLines={x => setField('purchases', x)} />
      <LineSection title="経費" lines={draft.expenses} updateLines={x => setField('expenses', x)} />
      <LineSection title="移動（場所を選択）" lines={draft.movements} updateLines={x => setField('movements', x)} locations={source?.locations} movement />
      <section className="dailyCash-section">
        <h3>現金で受け取った税（自動入力／修正可）</h3>
        <div className="dailyCash-line">
          <span className="dailyCash-label">通常販売の消費税</span>
          <MoneyInput value={draft.normalTaxOverride === null ? normalCashTaxAutomatic : draft.normalTaxOverride}
            onChange={v => setField('normalTaxOverride', v)} />
          <button className="dailyCash-smallButton" onClick={() => setField('normalTaxOverride', null)}>自動に戻す</button>
        </div>
        <div className="dailyCash-line">
          <span className="dailyCash-label">免税の仮受消費税（リファンド）</span>
          <MoneyInput value={draft.exemptProvisionalTaxOverride === null ? exemptProvisionalTaxAutomatic : draft.exemptProvisionalTaxOverride}
            onChange={v => setField('exemptProvisionalTaxOverride', v)} />
          <button className="dailyCash-smallButton" onClick={() => setField('exemptProvisionalTaxOverride', null)}>自動に戻す</button>
        </div>
        <div className="dailyCash-line"><strong className="dailyCash-label">税合計</strong><strong>{formatYen(tax)}</strong></div>
        <p className="dailyCash-help">※免税の仮受消費税は、実際に現金で預かった税額だけを集計します。従来の免税取引（消費税を預かっていないもの）は0円です。</p>
        {draft.taxOverride != null && draft.normalTaxOverride === null && draft.exemptProvisionalTaxOverride === null &&
          <p className="dailyCash-help">※旧版の「税」の手入力値は区分が不明なため引き継いでいません。必要なら新しい2欄に再入力してください。</p>}
      </section>
      <div className="dailyCash-totals">
        <div>本日（前回清算繰越） <strong>{formatYen(today)}</strong></div>
        <div>現金返品（自動計上） <strong>{formatYen(cashRefund)}</strong></div>
        <div>利益（移動差額込み） <strong>{formatYen(profit)}</strong></div>
        <div>通常販売の消費税 <strong>{formatYen(normalCashTax)}</strong></div>
        <div>免税の仮受消費税 <strong>{formatYen(exemptProvisionalCashTax)}</strong></div>
        <div>税合計 <strong>{formatYen(tax)}</strong></div>
        <div>帳簿上の現金 <strong>{formatYen(expected)}</strong></div>
        <div>数えた現金 <strong>{formatYen(countedTotal)}</strong></div>
        <div>過不足 <strong className={overShort !== 0 ? 'dailyCash-variance' : ''}>{formatYen(overShort)}</strong></div>
      </div>
      <div className="dailyCash-actions" style={{ flexWrap: 'wrap' }}>
        <button className="secondary" type="button" onClick={() => moveStep(1)}>← 現金確認へ</button>
        <button className="secondary" type="button" disabled={printingReceipt} onClick={() => printSettlementPreview(false)}>
          {printingReceipt ? '印刷中...' : '清算レシートを再印刷'}
        </button>
        <button type="button" onClick={() => moveStep(3)}>内訳を確認して印刷へ →</button>
      </div>
    </>}

    {step === 3 && <>
      <h3>印刷前の最終確認</h3>
      <div className="dailyCash-totals">
        <div>本日（前回清算繰越） <strong>{formatYen(today)}</strong></div>
        <div>販売合計 <strong>{formatYen(salesTotal)}</strong></div>
        <div>買取合計 <strong>{formatYen(buybackTotal)}</strong></div>
        <div>仕入れ合計 <strong>{formatYen(purchaseTotal)}</strong></div>
        <div>経費合計 <strong>{formatYen(expensesTotal)}</strong></div>
        <div>移動入金／出金 <strong>{formatYen(movementIn)}／{formatYen(movementOut)}</strong></div>
        <div>現金返品（自動計上） <strong>{formatYen(cashRefund)}</strong></div>
        <div>利益 <strong>{formatYen(profit)}</strong></div>
        <div>通常販売の消費税 <strong>{formatYen(normalCashTax)}</strong></div>
        <div>免税の仮受消費税 <strong>{formatYen(exemptProvisionalCashTax)}</strong></div>
        <div>税合計 <strong>{formatYen(tax)}</strong></div>
        <div>実際の現金 <strong>{formatYen(countedTotal)}</strong></div>
        <div>残金（1,000円未満） <strong>{formatYen(remainder)}</strong></div>
        <div>翌日（1,000円単位） <strong>{formatYen(nextAmount)}</strong></div>
        <div>過不足 <strong className={overShort !== 0 ? 'dailyCash-variance' : ''}>{formatYen(overShort)}</strong></div>
      </div>
      <p className="dailyCash-help">B5はElectronでプレビューなし直接印刷します。一括処理は正式清算も実行するため、処理内容を確認してから押してください。</p>
      {batchStatus && <p className="dailyCash-help" role="status">{batchStatus}</p>}
      <div className="dailyCash-actions" style={{ flexWrap: 'wrap' }}>
        <button className="secondary" type="button" disabled={printing} onClick={() => moveStep(2)}>← 内訳を修正</button>
        <button type="button" disabled={printing} onClick={onPrint}>
          {printing ? '処理中...' : '保存してB5直接印刷'}
        </button>
        <button type="button" disabled={printing} onClick={onPrintAndCloseAll}>
          {printing ? '処理中...' : 'B5印刷・清算・ジャーナル・ノート一括処理'}
        </button>
      </div>
    </>}
  </div>;
}

export default function DailyCashReportPage() {
  const { user: userInfo, loading } = useAuthContext();
  const [params] = useSearchParams();
  const date = params.get('date') || localDateString();
  const registerId = params.get('register_id') || userInfo?.register_id;
  if (loading) return <div className="dailyCash-page">読み込み中...</div>;
  if (!registerId) return <div className="dailyCash-page">レジIDが取得できません。レジにログインしてから開いてください。</div>;
  return <DailyCashEditor key={`${registerId}:${date}`} date={date} registerId={registerId} />;
}
